import { useCallback, useMemo } from "react";
import {
  CajaPrimaria,
  CajaPrimariaPagingRes,
  CajaPrimariaParams,
  Flota,
  FlotasPagingRes,
  IDataProducto, IFlotaParams, IProductoPagingRes, OLT, OLTPagingRes, ProductosParam
} from "../interfaces";
import {
  BODEGAS, BodegasParams,
  IBodegasPagingRes,
  IUbicacionesPagingRes, IUbicacionProducto, IUbicacionProductoPaginaRes, IUbicacionProductoParams, Ubicaciones,
  UbicacionesParams
} from "../interfaces/erp/inventario";
import { urlBackend } from "../constants";
import { genericFetch } from "./genericFetch";
import { erpApi } from "../axios";
import { IDataSerie, IDataSerieParams, ISeriePagingRes } from "../interfaces/erp/administration/productofactorserie.interface";
import { Empleado, EmpleadosPagingRes } from "../interfaces/erp/empleados";
import { useAuthStore } from "@/store/auth";

const OPCION_TODOS_PRODUCTO: any = {
  codigo: 'TODOS',
  descripcion: 'TODOS',
  id_producto: 'TODOS',
};

const OPCION_TODOS_UBICACION: Ubicaciones = {
  bodega: 'TODOS',
  estado: true,
  nombre: 'TODOS',
  codigo: 'TODOS',
  id_ubicacion: 'TODOS',
};

export const OPCION_TODOS_BODEGA: BODEGAS = {
  id_bodega: 'TODOS',
  estado: true,
  nombre: 'TODOS',
  codigo: 'TODOS',
  ciudad: 'TODOS',
};


const OPCION_TODOS_SERIE: IDataSerieParams = {
  id_series: 'TODOS',
  codigo: 'TODOS',
  serie: 'TODOS',
};

const OPCION_TODOS_FLOTA: IFlotaParams = {
  id_flota: 'TODOS',
  codigo: 'TODOS',
  nombre: 'TODOS'
};

const OPCION_TODOS_UBICACION_PRODUCTO: IUbicacionProductoParams = {
  id_ubicacion_producto: 'TODOS',
  codigo: 'TODOS',
  nombre: 'TODOS'
};


export const USE_TODOS = 'TODOS'

///* fetch para el autcomplete

const { get } = erpApi();

const fetchAutocompleteGeneric = async<
  TOption,
  TResponse extends { data: TOption[]; next?: string | null; count?: number }
>(
  url: string,
): Promise<{ options: TOption[]; hasMore: boolean }> => {

  const response = await get<TResponse>(`${url}`, true);
  const data = response.data;

  // Calcular hasMore basándose en la respuesta del backend
  // Si hay campo 'next', usarlo; si no, verificar si recibimos datos
  const hasMore = data.next != null || (data.data?.length ?? 0) >= 10;

  return {
    options: data.data as TOption[],
    hasMore: hasMore && (data.data?.length ?? 0) > 0,
  };
};

export default function useAutocompleteFetch() {


  ///* filtro general bodega

  const paramsBodega = useMemo<BodegasParams>(() => ({
    es_transito: false,
    es_cliente: false,
    es_concecion_cliente: false,
    estado: true
  }), [])

  /** fetch fetchGenericUbicacion */
  const fetchAutocompleteUbicacion = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof Ubicaciones = 'nombre',
      params: UbicacionesParams,
    ) => {
      return genericFetch<
        Ubicaciones,
        UbicacionesParams,
        keyof Ubicaciones & string
      >({
        baseUrl: `${urlBackend}api/v1/ubicacion/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<Ubicaciones, IUbicacionesPagingRes>(url),
        allOption: OPCION_TODOS_UBICACION,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof Ubicaciones & string,
        params
      });
    },
    [urlBackend],
  );

  ///* fetch fetchGenericUbicacion
  const fetchAutocompleteBodega = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof BODEGAS = 'nombre',
      params: BodegasParams,
    ) => {
      return genericFetch<
        BODEGAS,
        BodegasParams,
        keyof BODEGAS & string
      >({
        baseUrl: `${urlBackend}api/v1/bodega/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<BODEGAS, IBodegasPagingRes>(url),
        // NO pasamos allOption aquí; el "TODOS" se agrega abajo SOLO si el usuario
        // tiene acceso a más de una bodega (el backend ya filtra por perfil), para
        // no mostrar "TODOS" a usuarios restringidos / sin permisos de bodega.
        search,
        page,
        isAll,
        keySearch: keySearch as keyof BODEGAS & string,
        params: {
          ...params,
          ...paramsBodega
        },
      }).then((res) => {
        // "TODOS" SOLO para superusuario (ve TODAS las bodegas del sistema). Un
        // usuario restringido ve únicamente sus bodegas asignadas (origen/destino),
        // igual que el backend, que solo hace bypass del filtro para is_superuser.
        const u = useAuthStore.getState().user as any;
        if (page === 1 && isAll && !!u?.is_superuser) {
          return { ...res, options: [OPCION_TODOS_BODEGA, ...res.options] };
        }
        return res;
      });
    },
    [urlBackend],
  );

  ///* fetch fetchGenericUbicacion
  const fetchAutocompleteProducto = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof IDataProducto = 'codigo',
      params: ProductosParam,
    ) => {
      return genericFetch<
        IDataProducto,
        ProductosParam,
        keyof IDataProducto & string
      >({
        baseUrl: `${urlBackend}api/v1/producto-simple/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<IDataProducto, IProductoPagingRes>(url),
        allOption: OPCION_TODOS_PRODUCTO,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof IDataProducto & string,
        params
      });
    },
    [urlBackend],
  );


  /** fetch fetchGenericUbicacion */
  const fetchAutocompleteSerie = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof IDataSerie = 'serie',
      params: IDataSerieParams,
    ) => {
      return genericFetch<
        IDataSerie,
        IDataSerieParams,
        keyof IDataSerie & string
      >({
        baseUrl: `${urlBackend}api/v1/series/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<IDataSerie, ISeriePagingRes>(url),
        allOption: OPCION_TODOS_SERIE as IDataSerie,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof IDataSerie & string,
        params
      });
    },
    [urlBackend],
  );


  const fetchAutocompleteFlota = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof Flota = 'nombre',
      params: IFlotaParams & { disable_all_option?: boolean },
    ) => {
      return genericFetch<
        Flota,
        IFlotaParams,
        keyof Flota & string
      >({
        baseUrl: `${urlBackend}api/v1/flota/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<Flota, FlotasPagingRes>(url),
        ...(params?.disable_all_option? 
          { }
          :{ allOption: OPCION_TODOS_FLOTA as Flota }
        ),
        search,
        page,
        isAll,
        keySearch: keySearch as keyof Flota & string,
        params
      });
    },
    [urlBackend],
  );


  const fetchAutocompleteUbicacionProducto = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof IUbicacionProducto = 'nombre',
      params: IUbicacionProductoParams,
    ) => {
      return genericFetch<
        IUbicacionProducto,
        IUbicacionProductoParams,
        keyof IUbicacionProducto & string
      >({
        baseUrl: `${urlBackend}api/v1/ubicacion-producto-simple/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<IUbicacionProducto, IUbicacionProductoPaginaRes>(url),
        allOption: OPCION_TODOS_UBICACION_PRODUCTO as IUbicacionProducto,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof IUbicacionProducto & string,
        params
      });
    },
    [urlBackend],
  );




    /** fetch fetchAutocompleteCajaTroncal */
  const fetchAutocompleteCajaTroncal = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof CajaPrimaria = 'nombre',
      params: CajaPrimariaParams,
    ) => {
      return genericFetch<
        CajaPrimaria,
        CajaPrimariaParams,
        keyof CajaPrimaria & string
      >({
        baseUrl: `${urlBackend}api/v1/cajaprimaria/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<CajaPrimaria, CajaPrimariaPagingRes>(url),
        // allOption: OPCION_TODOS_UBICACION,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof CajaPrimaria & string,
        params: {
          ...params,
          estado: true,
        },
      });
    },
    [urlBackend],
  );



     /** fetch fetchAutocompleteCajaTroncal */
  const fetchAutocompleteOLT = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof OLT = 'nombre',
      params: Partial<OLT>,
    ) => {
      return genericFetch<
        OLT,
        Partial<OLT>,
        keyof OLT & string
      >({
        baseUrl: `${urlBackend}api/v1/olt/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<OLT, OLTPagingRes>(url),
        // allOption: OPCION_TODOS_UBICACION,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof OLT & string,
        params: {
          ...params,
          estado: true,
        },
      });
    },
    [urlBackend],
  );


  const fetchAutocompleteEmpleado = useCallback(
    (
      search: string,
      page: number,
      isAll: boolean,
      keySearch: keyof Empleado = 'user',
      params: Partial<Empleado>,
    ) => {
      return genericFetch<
        Empleado,
        Partial<Empleado>,
        keyof Empleado & string
      >({
        baseUrl: `${urlBackend}api/v1/empleado/`,
        fetchFn: (url) =>
          fetchAutocompleteGeneric<Empleado, EmpleadosPagingRes>(url),
        // allOption: OPCION_TODOS_UBICACION,
        search,
        page,
        isAll,
        keySearch: keySearch as keyof Empleado & string,
        params: {
          ...params,
          estado: true,
        },
      });
    },
    [urlBackend],
  );

  return {
    fetchAutocompleteUbicacion,
    fetchAutocompleteBodega,
    fetchAutocompleteProducto,
    fetchAutocompleteSerie,
    fetchAutocompleteCajaTroncal,
    fetchAutocompleteFlota,
    fetchAutocompleteUbicacionProducto,
    fetchAutocompleteOLT,
    fetchAutocompleteEmpleado,
  }

}